import React, { useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import useFormStyles from "../../styles/ncp2026/form";
import {
    Button, CircularProgress,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    Paper, Switch,
    TextField,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box
} from "@mui/material";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import { useAppDispatch, useAppSelector } from '../../stores/store';
import { insertNcp2026, Ncp2026PayloadProps, clearError } from '../../features/ncp/ncp2026Slice';
import { showNotification } from '../../features/notification/notificationSlice';

interface FormProps {
    partner: string;
}

const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = error => reject(error);
    });
};

const maxFileSize = 10 * 1024 * 1024; // 10MB

const Form: React.FC<FormProps> = ({ partner }) => {
    const dispatch = useAppDispatch();
    const { loading, error: apiError } = useAppSelector(state => state.ncp2026);
    const formStyles = useFormStyles();

    const [birthdate, setBirthdate] = useState<Dayjs | null>(null);
    const [lastname, setLastname] = useState<string>('');
    const [firstname, setFirstname] = useState<string>('');
    const [phone, setPhone] = useState<string>('');
    const [receiptNr, setReceiptNr] = useState<string>('');
    const [receiptDate, setReceiptDate] = useState<Dayjs | null>(null);
    const [receiptFile, setReceiptFile] = useState<File | null>(null);
    const [accordingRules, setAccordingRules] = useState<boolean>(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [isDatePickerBirthdateOpen, setIsDatePickerBirthdateOpen] = useState(false);
    const [isDatePickerReceiptDateOpen, setIsDatePickerReceiptDateOpen] = useState(false);
    const [isPhotoSourceDialogOpen, setIsPhotoSourceDialogOpen] = useState(false);

    const fileInputRef = React.createRef<HTMLInputElement>();
    const cameraInputRef = React.createRef<HTMLInputElement>();

    const regulamentUrl = partner === 'carrefour'
        ? '/pdf/2026/Regulament_KOZEL_campanie MT_Poti castiga premii pe gustul tau_CRF martie 2026.pdf'
        : partner === 'kaufland'
            ? '/pdf/2026/Regulament_KOZEL_campanie MT_Poti castiga premii pe gustul tau_Kaufland.pdf'
            : '/pdf/2026/Regulament_KOZEL_Mega si Profi_Poti castiga premii pe gustul tau.pdf';

    const partnerBottomText: Record<string, string> = {
        carrefour:
            'Perioada campaniei: 11.03.2026 – 31.05.2026.\n' +
            'Consultă gratuit regulamentul promoției pe www.kozel.ro/regulamente. Produsele participante în campanie sunt: orice combinație de produse Kozel Lager și/sau Kozel Dark în valoare de minimum 25 lei. Se pot înscrie bonuri fiscale doar din locațiile participante. Numărul total de premii este de 15, cu o valoare totală netă comercială estimativă de 4.600 lei TVA inclus. Premiile se vor acorda prin tragere la sorți din baza de înscrieri din locațiile participante Carrefour. Marele Premiu se acordă la nivel național, prin tragere la sorți, din baza totală de înscrieri în campanie. Se acordă un singur premiu mare, sub formă de voucher cadou, cu o valoare totală comercială estimativă de 25.000 lei. Voucherul cadou poate fi consumat pentru orice experiențe de pe site-ul www.complice.ro. Lista locațiilor participante poate fi consultată în regulamentul campaniei. Păstrați bonul fiscal în original pentru validare.',
        kaufland:
            'Perioada campaniei: 11.03.2026 – 31.05.2026.\n' +
            'Consultă gratuit regulamentul promoției pe www.kozel.ro/regulamente. Produsele participante în campanie sunt: orice combinație de produse Kozel Lager și/sau Kozel Dark în valoare de minimum 25 lei. Se pot înscrie bonuri fiscale doar din locațiile participante. Numărul total de premii este de 15, cu o valoare totală netă comercială estimativă de 6.700 lei TVA inclus. Premiile se vor acorda prin tragere la sorți din baza de înscrieri din locațiile participante Kaufland. Marele Premiu se acordă la nivel național, prin tragere la sorți, din baza totală de înscrieri în campanie. Se acordă un singur premiu mare, sub formă de voucher cadou, cu o valoare totală comercială estimativă de 25.000 lei. Voucherul cadou poate fi consumat pentru orice experiențe de pe site-ul www.complice.ro. Lista locațiilor participante poate fi consultată în regulamentul campaniei. Păstrați bonul fiscal în original pentru validare.',
        mega:
            'Perioada campaniei: 09.03.2026 – 31.05.2026.\n' +
            'Consultă gratuit regulamentul promoției pe www.kozel.ro/regulamente. Produsele participante în campanie sunt: orice combinație de produse Kozel Lager și/sau Kozel Dark în valoare de minimum 25 lei. Se pot înscrie bonuri fiscale doar din locațiile participante. Numărul total de premii este de 20, cu o valoare totală netă comercială estimativă de 11.000 lei TVA inclus. Premiile se vor acorda prin tragere la sorți din baza de înscrieri din locațiile participante Mega Image și Profi. Marele Premiu se acordă la nivel național, prin tragere la sorți, din baza totală de înscrieri în campanie. Se acordă un singur premiu mare, sub formă de voucher cadou, cu o valoare totală comercială estimativă de 25.000 lei. Voucherul cadou poate fi consumat pentru orice experiențe de pe site-ul www.complice.ro. Lista locațiilor participante poate fi consultată în regulamentul campaniei. Păstrați bonul fiscal în original pentru validare.',
        profi:
            'Perioada campaniei: 09.03.2026 – 31.05.2026.\n' +
            'Consultă gratuit regulamentul promoției pe www.kozel.ro/regulamente. Produsele participante în campanie sunt: orice combinație de produse Kozel Lager și/sau Kozel Dark în valoare de minimum 25 lei. Se pot înscrie bonuri fiscale doar din locațiile participante. Numărul total de premii este de 20, cu o valoare totală netă comercială estimativă de 11.000 lei TVA inclus. Premiile se vor acorda prin tragere la sorți din baza de înscrieri din locațiile participante Mega Image și Profi. Marele Premiu se acordă la nivel național, prin tragere la sorți, din baza totală de înscrieri în campanie. Se acordă un singur premiu mare, sub formă de voucher cadou, cu o valoare totală comercială estimativă de 25.000 lei. Voucherul cadou poate fi consumat pentru orice experiențe de pe site-ul www.complice.ro. Lista locațiilor participante poate fi consultată în regulamentul campaniei. Păstrați bonul fiscal în original pentru validare.',
    };

    const validateFields = () => {
        const newErrors: { [key: string]: string } = {};

        if (!birthdate) newErrors.birthdate = 'Data nașterii este obligatorie';
        if (!lastname) newErrors.lastname = 'Numele este obligatoriu';
        if (!firstname) newErrors.firstname = 'Prenumele este obligatoriu';
        if (!phone) newErrors.phone = 'Numărul de telefon este obligatoriu';
        if (!receiptNr) newErrors.receiptNr = 'Numărul bonului fiscal este obligatoriu';
        if (!receiptDate) newErrors.receiptDate = 'Data bonului fiscal este obligatorie';
        if (!receiptFile) newErrors.receiptFile = 'Bonul fiscal trebuie încărcat';
        if (!accordingRules) newErrors.accordingRules = 'Trebuie să acceptați regulamentul';

        setErrors(newErrors);
        validateBirthdate(birthdate);
        validatePhone(phone);
        validateReceiptNr(receiptNr);
        validateFileSize(receiptFile);

        return Object.keys(newErrors).length === 0;
    };

    const validateFileSize = (file: File | null) => {
        if (file && file.size > maxFileSize) {
            setErrors(prevErrors => ({
                ...prevErrors,
                receiptFile: 'Fișierul nu trebuie să fie mai mare de 10MB.',
            }));
        }
    };

    const validatePhone = (phoneNr: string) => {
        if (phoneNr) {
            if (phoneNr.length !== 10) {
                setErrors(prevErrors => ({ ...prevErrors, phone: 'Numărul de telefon trebuie sa fie de 10 cifre' }));
                return false;
            } else {
                setErrors(prevErrors => ({ ...prevErrors, phone: '' }));
                return true;
            }
        }
    };

    const validateReceiptNr = (receiptNumber: string) => {
        if (receiptNumber && receiptNumber.length > 12) {
            setErrors(prevErrors => ({
                ...prevErrors,
                receiptNr: 'Numărul bonului fiscal nu trebuie să depășească 12 cifre',
            }));
            return false;
        }
        return true;
    };

    const validateBirthdate = (date: Dayjs | null) => {
        if (date) {
            const age = dayjs().diff(date, 'year');
            if (age < 18) {
                setErrors(prevErrors => ({
                    ...prevErrors,
                    birthdate: 'Trebuie să aveți cel puțin 18 ani.',
                }));
            } else {
                setErrors(prevErrors => ({ ...prevErrors, birthdate: '' }));
            }
        }
    };

    React.useEffect(() => {
        if (apiError) {
            setErrors(prevErrors => ({
                ...prevErrors,
                submit: apiError
            }));
        }
    }, [apiError]);


    const resetState = () => {
        setBirthdate(null);
        setLastname('');
        setFirstname('');
        setPhone('');
        setReceiptNr('');
        setReceiptDate(null);
        setReceiptFile(null);
        setAccordingRules(false);
        setErrors({});
        dispatch(clearError());
    };

    const handleSubmit = async () => {
        dispatch(clearError());

        if (validateFields()) {
            try {
                if (!receiptFile || !birthdate || !receiptDate) {
                    return;
                }
                const receiptFileBase64 = await convertFileToBase64(receiptFile);

                const data: Ncp2026PayloadProps = {
                    birthdate: birthdate.format('YYYY-MM-DD'),
                    lastname,
                    firstname,
                    phone,
                    receiptNr,
                    receiptDate: receiptDate.format('YYYY-MM-DD'),
                    receiptFile: receiptFileBase64,
                    magazin: partner,
                };

                const result = await dispatch(insertNcp2026(data));

                if (insertNcp2026.fulfilled.match(result)) {
                    resetState();
                }

            } catch (error) {
                console.error("Error converting file to Base64:", error);
            }
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files ? event.target.files[0] : null;
        if (file) {
            if (file.size > maxFileSize) {
                setErrors(prevErrors => ({
                    ...prevErrors,
                    receiptFile: 'Fișierul nu trebuie să fie mai mare de 10MB.',
                }));
            } else {
                setErrors(prevErrors => ({ ...prevErrors, receiptFile: '' }));
                setReceiptFile(file);
            }
        }
    };

    const handleFileInputClick = (e: any) => {
        e.stopPropagation();
        setIsPhotoSourceDialogOpen(true);
    };

    const handleGallerySelect = () => {
        setIsPhotoSourceDialogOpen(false);
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleCameraSelect = async () => {
        try {
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                dispatch(showNotification({
                    message: 'Dispozitivul tău nu suportă deschiderea camerei.',
                    variant: 'error'
                }));
                return;
            }
            await navigator.mediaDevices.getUserMedia({ video: true });
            setIsPhotoSourceDialogOpen(false);
            if (cameraInputRef.current) {
                cameraInputRef.current.click();
            }
        } catch (error) {
            setIsPhotoSourceDialogOpen(false);
            dispatch(showNotification({
                message: 'Nu am permisiunea să accesez camera. Te rugăm să o activezi din setările browserului.',
                variant: 'error'
            }));
        }
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (/^\d{0,10}$/.test(value)) {
            setPhone(value);
        }
    };

    const handleReceiptNrChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (/^\d*$/.test(value)) {
            setReceiptNr(value);
            validateReceiptNr(value);
        }
    };

    return (
        <div className={formStyles.formWrapper}>
            <Grid container direction="column" alignItems="center">
                <Grid item xs={12} className={formStyles.boxTitle}>
                    <Typography className={formStyles.title}>FORMULAR DE ÎNSCRIERE</Typography>
                </Grid>

                <Grid item xs={12} style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                    <Paper className={formStyles.paperForm}>
                        <DatePicker
                            value={birthdate}
                            onAccept={(newValue) => {
                                setBirthdate(newValue);
                                validateBirthdate(newValue);
                                setIsDatePickerBirthdateOpen(false);
                            }}
                            label={'Data nașterii*'}
                            open={isDatePickerBirthdateOpen}
                            closeOnSelect={true}
                            onOpen={() => setIsDatePickerBirthdateOpen(true)}
                            onClose={() => setIsDatePickerBirthdateOpen(false)}
                            format={'DD/MM/YYYY'}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    error: !!errors.birthdate,
                                    helperText: errors.birthdate,
                                    className: formStyles.formInput,
                                    variant: 'outlined',
                                    color: 'primary',
                                    onClick: (e) => {
                                        e.preventDefault();
                                        setIsDatePickerBirthdateOpen(true);
                                    },
                                },
                            }}
                        />

                        <TextField
                            value={lastname}
                            onChange={e => setLastname(e.target.value)}
                            fullWidth
                            label={'Nume*'}
                            error={!!errors.lastname}
                            helperText={errors.lastname}
                            className={formStyles.formInput}
                            variant="outlined"
                            color={'primary'}
                        />

                        <TextField
                            value={firstname}
                            onChange={e => setFirstname(e.target.value)}
                            fullWidth
                            label={'Prenume*'}
                            error={!!errors.firstname}
                            helperText={errors.firstname}
                            className={formStyles.formInput}
                            variant="outlined"
                            color={'primary'}
                        />

                        <TextField
                            value={phone}
                            onChange={handlePhoneChange}
                            fullWidth
                            label={'Număr de telefon*'}
                            error={!!errors.phone}
                            helperText={errors.phone}
                            className={formStyles.formInput}
                            variant="outlined"
                            color={'primary'}
                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                        />

                        <TextField
                            value={receiptNr}
                            onChange={handleReceiptNrChange}
                            fullWidth
                            label={'Număr bon fiscal*'}
                            error={!!errors.receiptNr}
                            helperText={errors.receiptNr}
                            className={formStyles.formInput}
                            variant="outlined"
                            color={'primary'}
                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                        />

                        <DatePicker
                            value={receiptDate}
                            onAccept={(newValue) => {
                                setReceiptDate(newValue);
                                setIsDatePickerReceiptDateOpen(false);
                            }}
                            label={'Data bon fiscal*'}
                            open={isDatePickerReceiptDateOpen}
                            closeOnSelect={true}
                            onOpen={() => setIsDatePickerReceiptDateOpen(true)}
                            onClose={() => setIsDatePickerReceiptDateOpen(false)}
                            maxDate={dayjs()}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    error: !!errors.receiptDate,
                                    helperText: errors.receiptDate,
                                    className: formStyles.formInput,
                                    variant: 'outlined',
                                    color: 'primary',
                                    onClick: (e) => {
                                        e.preventDefault();
                                        setIsDatePickerReceiptDateOpen(true);
                                    },
                                },
                            }}
                        />

                        <TextField
                            fullWidth
                            label={'Încarcă bonul fiscal*'}
                            error={!!errors.receiptFile}
                            helperText={errors.receiptFile}
                            className={formStyles.formInput}
                            variant="outlined"
                            color={'primary'}
                            onClick={handleFileInputClick}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={handleFileInputClick}>
                                            <InsertPhotoIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                readOnly: true
                            }}
                            value={receiptFile ? receiptFile.name : ''}
                        />

                        <input
                            type="file"
                            onChange={handleFileChange}
                            accept="image/*"
                            style={{ display: 'none' }}
                            ref={fileInputRef}
                        />

                        <input
                            type="file"
                            onChange={handleFileChange}
                            accept="image/*"
                            capture="environment"
                            style={{ display: 'none' }}
                            ref={cameraInputRef}
                        />

                        <Dialog
                            open={isPhotoSourceDialogOpen}
                            onClose={() => setIsPhotoSourceDialogOpen(false)}
                            maxWidth="xs"
                            fullWidth
                        >
                            <DialogTitle sx={{ textAlign: 'center', color: 'white' }}>
                                Cum dorești să încarci poza?
                            </DialogTitle>
                            <DialogContent>
                                <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                                    <Button
                                        variant="contained"
                                        onClick={handleGallerySelect}
                                        sx={{ width: '50%' }}
                                        color="secondary"
                                    >
                                        Galerie
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={handleCameraSelect}
                                        sx={{ width: '50%' }}
                                        color="secondary"
                                    >
                                        Cameră
                                    </Button>
                                </Box>
                            </DialogContent>
                            <DialogActions sx={{ justifyContent: 'center' }}>
                                <Button
                                    onClick={() => setIsPhotoSourceDialogOpen(false)}
                                    variant="contained"
                                    sx={{ width: '50%' }}
                                >
                                    Anulează
                                </Button>
                            </DialogActions>
                        </Dialog>

                        <FormControlLabel
                            className={formStyles.formControlCheckbox}
                            labelPlacement={'start'}
                            control={
                                <Switch
                                    checked={accordingRules}
                                    onChange={e => setAccordingRules(e.target.checked)}
                                    inputProps={{ 'aria-label': 'controlled' }}
                                />
                            }
                            label={
                                <Typography className={formStyles.formControlCheckboxLabel}>
                                    Am citit integral, am înțeles și sunt de acord cu prevederile
                                    <a target={'_blank'} rel="noreferrer" href={regulamentUrl}> Regulamentului Campaniei</a>
                                    {(partner === 'mega' || partner === 'profi') && (
                                        <> și <a target={'_blank'} rel="noreferrer" href="/pdf/2026/Act aditional 1_KOZEL_Mega si Profi_Poti castiga premii pe gustul tau.pdf"> Actului Adițional</a></>
                                    )}
                                </Typography>
                            }
                        />
                        {errors.accordingRules && <Typography color="error">{errors.accordingRules}</Typography>}

                        {apiError && (
                            <Typography color="error" style={{ marginTop: '10px', marginBottom: '10px' }}>
                                {apiError}
                            </Typography>
                        )}

                        <div className={formStyles.submitButtonContainer}>
                            {loading ? (
                                <Button className={formStyles.submitButton}><CircularProgress /></Button>
                            ) : (
                                <Button onClick={handleSubmit} className={formStyles.submitButton}>
                                    ÎNSCRIE BONUL
                                </Button>
                            )}
                        </div>
                    </Paper>
                </Grid>

                <Grid item xs={12} className={formStyles.bottomText}>
                    <Typography className={formStyles.bottomTextContent}>
                        {partnerBottomText[partner]}
                    </Typography>
                </Grid>
            </Grid>
        </div>
    );
}

export default Form;